import React, { useState } from 'react';
import { Send, Loader2, CheckCircle, AlertCircle, User, Mail, Phone, MessageSquare } from 'lucide-react';

/**
 * Formulario de registro para congresos
 * Se conecta con la API /api/registro-congresos.php
 */
const FormularioRegistroCongreso = ({ congresoNombre, congresoId }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    howDidYouHear: '',
    message: '',
    privacyPolicy: false,
    tipoEvento: 'congresos' // 'congresos' o 'foro'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validación básica
    if (!formData.name.trim()) {
      setSubmitStatus('error');
      setErrorMessage('El nombre es requerido');
      return;
    }
    
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setSubmitStatus('error');
      setErrorMessage('Email no válido');
      return;
    }
    
    if (!formData.phone.trim()) {
      setSubmitStatus('error');
      setErrorMessage('El teléfono es requerido');
      return;
    }
    
    if (!formData.privacyPolicy) {
      setSubmitStatus('error');
      setErrorMessage('Debe aceptar la política de privacidad');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage('');

    try {
      // Agregar información del congreso al comentario
      const dataToSend = {
        ...formData,
        message: `[Congreso: ${congresoNombre || congresoId}]\n${formData.message}`
      };

      const response = await fetch('/api/registro-congresos.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dataToSend)
      });

      const data = await response.json();

      if (data.respuesta === 'Ok') {
        setSubmitStatus('success');
        setSuccessMessage(data.mensaje || '¡Registro exitoso! Se ha enviado un correo con sus credenciales de acceso.');
        
        // Limpiar formulario
        setFormData({
          name: '',
          email: '',
          phone: '',
          howDidYouHear: '',
          message: '',
          privacyPolicy: false
        });

        // Scroll al mensaje de éxito
        setTimeout(() => {
          const successElement = document.getElementById('registro-success');
          if (successElement) {
            successElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      } else {
        setSubmitStatus('error');
        setErrorMessage(data.mensaje_error || 'Error al procesar el registro');
      }
    } catch (error) {
      console.error('Error en registro:', error);
      setSubmitStatus('error');
      setErrorMessage('Error de conexión. Por favor, intente nuevamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-8 border-l-4 border-[#0b742f]">
      <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
        <Send className="w-7 h-7 text-[#0b742f]" />
        Formulario de Registro
      </h3>

      {/* Mensaje de éxito */}
      {submitStatus === 'success' && (
        <div id="registro-success" className="bg-green-50 border-2 border-green-500 text-green-800 p-4 rounded-lg flex items-start gap-3 mb-6">
          <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">¡Registro exitoso!</p>
            <p className="text-sm mt-1">{successMessage}</p>
            <p className="text-sm mt-2 text-green-700">
              Revise su correo electrónico (incluya la carpeta de spam) para obtener sus credenciales de acceso a la plataforma.
            </p>
          </div>
        </div>
      )}

      {/* Mensaje de error */}
      {submitStatus === 'error' && (
        <div className="bg-red-50 border-2 border-red-500 text-red-800 p-4 rounded-lg flex items-start gap-3 mb-6">
          <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">Error en el registro</p>
            <p className="text-sm mt-1">{errorMessage}</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Tipo de Evento */}
        <div>
          <label className="block text-gray-700 font-semibold mb-2">
            Tipo de evento <span className="text-red-600">*</span>
          </label>
          <select
            name="tipoEvento"
            value={formData.tipoEvento}
            onChange={handleChange}
            required
            disabled={isSubmitting}
            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#0b742f] focus:outline-none transition-colors disabled:bg-gray-100"
          >
            <option value="congresos">Congresos de Investigación CENID</option>
            <option value="foro">II Foro Iberoamericano de Cátedras UNESCO</option>
          </select>
          <p className="text-sm text-gray-600 mt-1">
            {formData.tipoEvento === 'congresos' 
              ? '📅 27 y 28 de noviembre de 2025' 
              : '📅 6-8 de Mayo, 2026'}
          </p>
        </div>

        {/* Nombre completo */}
        <div>
          <label className="block text-gray-700 font-semibold mb-2">
            Nombre completo <span className="text-red-600">*</span>
          </label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              disabled={isSubmitting}
              className="w-full pl-11 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#0b742f] focus:outline-none transition-colors disabled:bg-gray-100"
              placeholder="Ingrese su nombre completo"
            />
          </div>
        </div>

        {/* Email */}
        <div>
          <label className="block text-gray-700 font-semibold mb-2">
            Correo electrónico <span className="text-red-600">*</span>
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              disabled={isSubmitting}
              className="w-full pl-11 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#0b742f] focus:outline-none transition-colors disabled:bg-gray-100"
              placeholder="correo@ejemplo.com"
            />
          </div>
        </div>

        {/* Teléfono */}
        <div>
          <label className="block text-gray-700 font-semibold mb-2">
            Teléfono <span className="text-red-600">*</span>
          </label>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
              disabled={isSubmitting}
              className="w-full pl-11 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#0b742f] focus:outline-none transition-colors disabled:bg-gray-100"
              placeholder="+52 55 1234 5678"
            />
          </div>
        </div>

        {/* ¿Cómo nos conoció? */}
        <div>
          <label className="block text-gray-700 font-semibold mb-2">
            ¿Cómo nos conoció?
          </label>
          <select
            name="howDidYouHear"
            value={formData.howDidYouHear}
            onChange={handleChange}
            disabled={isSubmitting}
            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#0b742f] focus:outline-none transition-colors disabled:bg-gray-100"
          >
            <option value="">Seleccione una opción</option>
            <option value="Redes sociales">Redes sociales</option>
            <option value="Página web">Página web</option>
            <option value="Recomendación">Recomendación</option>
            <option value="Correo electrónico">Correo electrónico</option>
            <option value="Evento anterior">Evento anterior</option>
            <option value="Búsqueda en Google">Búsqueda en Google</option>
            <option value="Otro">Otro</option>
          </select>
        </div>

        {/* Mensaje / Comentarios */}
        <div>
          <label className="block text-gray-700 font-semibold mb-2">
            Comentarios adicionales
          </label>
          <div className="relative">
            <MessageSquare className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows={4}
              disabled={isSubmitting}
              className="w-full pl-11 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#0b742f] focus:outline-none transition-colors resize-none disabled:bg-gray-100"
              placeholder="Mensaje adicional (opcional)"
            />
          </div>
        </div>

        {/* Checkbox de política de privacidad */}
        <div>
          <label className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg border-2 border-gray-200 cursor-pointer hover:border-[#0b742f] transition-colors">
            <input
              type="checkbox"
              name="privacyPolicy"
              checked={formData.privacyPolicy}
              onChange={handleChange}
              required
              disabled={isSubmitting}
              className="w-5 h-5 text-[#0b742f] mt-0.5 flex-shrink-0 disabled:opacity-50"
            />
            <span className="text-gray-700 text-sm">
              <span className="text-red-600 font-semibold">* Acepto:</span> Con el envío de este formulario acepto el tratamiento y gestión de los datos personales según la Política de Privacidad del CENID.
            </span>
          </label>
        </div>

        {/* Nota informativa */}
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
          <p className="text-sm text-blue-800">
            <strong>Nota:</strong> Al registrarse recibirá un correo electrónico con sus credenciales de acceso a la plataforma del congreso. Por favor, revise también su carpeta de spam.
          </p>
        </div>

        {/* Botón de envío */}
        <div className="flex justify-center pt-4">
          <button
            type="submit"
            disabled={isSubmitting}
            className="bg-gradient-to-r from-[#0b742f] to-[#085a24] text-white px-8 py-4 rounded-lg font-bold text-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-6 h-6 animate-spin" />
                Registrando...
              </>
            ) : (
              <>
                <Send className="w-6 h-6" />
                Registrarse
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default FormularioRegistroCongreso;
